import type { Dispatch, SetStateAction } from 'react';
import type { Session, User } from '@supabase/supabase-js';

export const APPLICATION_ROLES = [
  'admin',
  'attorney',
  'paralegal',
  'client',
  'lead',
  'agent',
  'team_member',
  'tech',
  'hr',
  'marketing',
] as const;

export type ApplicationRole = (typeof APPLICATION_ROLES)[number];

export interface AuthenticatedProfile {
  id: string;
  email: string | null;
  full_name: string | null;
  avatar_url: string | null;
  roles: ApplicationRole[];
  home_route?: string | null;
  // This browser's homepage, which takes precedence over home_route here.
  browser_home_route?: string | null;
  status?: string | null;
  firm_id?: string | null;
}

// The person's own profile fields, before the active firm supplies roles and status.
export type AuthenticatedIdentity = Omit<
  AuthenticatedProfile,
  'browser_home_route' | 'firm_id' | 'roles' | 'status'
>;

export type FirmMembershipStatus = 'active' | 'inactive';

// One of the caller's own firm memberships. The firm's name and number are null
// when the firm is unreadable, which is the case for an inactive membership. A
// platform administrator may suspend a firm; nobody can work in it then.
export interface FirmMembership {
  firmId: string;
  firmName: string | null;
  firmNumber: number | null;
  firmSuspended?: boolean;
  roles: ApplicationRole[];
  status: FirmMembershipStatus;
}

export interface ActiveFirm {
  id: string;
  name: string;
}

export interface SessionProfileSnapshot {
  full_name: string | null;
  roles: ApplicationRole[];
  avatar_url?: string | null;
  firm_id?: string | null;
}

export interface SavedSession {
  email?: string;
  fullName?: string | null;
  avatarUrl?: string | null;
  roles?: ApplicationRole[];
  firmId?: string | null;
  userId?: string;
  sessionData?: string;
}

export interface AuthenticatedSessionState {
  authenticatedReturnReady: boolean;
  authenticationError: string | null;
  clearAuthenticationError: () => void;
  clearSessionState: () => void;
  loading: boolean;
  memberships: FirmMembership[];
  profile: AuthenticatedProfile | null;
  refreshProfile: () => Promise<void>;
  setActiveFirm: (firmId: string) => void;
  setAuthenticatedHomeRoute: (route: string) => Promise<void>;
  // null returns this browser to the account homepage.
  setBrowserHomeRoute: (route: string | null) => void;
  savedSessions: SavedSession[];
  session: Session | null;
  setSavedSessions: Dispatch<SetStateAction<SavedSession[]>>;
}

export type AuthenticatedUserIdentity = Pick<User, 'id' | 'email'>;

export interface AuthStorage {
  getItem: (key: string) => string | null;
  removeItem: (key: string) => void;
  setItem: (key: string, value: string) => void;
}

export interface AuthBrowserStorageAccess {
  localStorage?: AuthStorage;
  sessionStorage?: AuthStorage;
}
